'use client';

import type { IconButtonProps } from '@mui/material/IconButton';
import IconButton from '@mui/material/IconButton';

import { varAlpha } from 'minimal-shared/utils';
import { useBoolean } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Avatar from '@mui/material/Avatar';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { usePathname } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { _mock } from 'src/_mock';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { AnimateBorder } from 'src/components/animate';

import { useAuthContext } from 'src/auth/hooks';
import { AccountButton } from './account-button';
import { LoggaUtKnapp } from './logga-ut-knapp';
import React, { useEffect } from "react";
import { useForm } from 'react-hook-form';
import { Form } from 'src/components/hook-form';
import { RHFUploadAvatar } from 'src/components/hook-form/rhf-upload';
import { laddaUppAvatar } from 'src/actions/anvandare';

// ----------------------------------------------------------------------

export type AccountDrawerProps = IconButtonProps & {
    data?: {
        label: string;
        href: string;
        icon?: React.ReactNode;
        info?: React.ReactNode;
    }[];
};

export function AccountDrawer({ data = [], sx, ...other }: AccountDrawerProps) {
    const pathname = usePathname();
    const { anvandare, checkUserSession } = useAuthContext();

    const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();

    const methods = useForm<{ avatarUrl: string | File | null }>({
        defaultValues: {
            avatarUrl: anvandare?.avatarUrl || null,
        },
    });

    const { setValue, reset } = methods;

    useEffect(() => {
        if (anvandare) {
            reset({ avatarUrl: anvandare.avatarUrl });
        }
    }, [anvandare, reset]);

    const onDropAvatar = React.useCallback(
        async (acceptedFiles: File[]) => {
            const file = acceptedFiles[0];

            if (file) {
                const newFile = Object.assign(file, {
                    preview: URL.createObjectURL(file),
                });
                setValue('avatarUrl', newFile, { shouldValidate: true });

                try {
                    await laddaUppAvatar(file);
                    if (checkUserSession) {
                        await checkUserSession(); // This will re-fetch /api/anvandare/v1
                    }
                } catch (error) {
                    console.error('Failed to upload avatar:', error);
                }
            }
        },
        [setValue, checkUserSession]
    );

    const renderAvatar = () => (
        <Box sx={{ mb: 2, p: 0.5, borderRadius: '50%', width: 108, height: 108, border: (theme) => `1px dashed ${theme.vars.palette.grey[500]}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <RHFUploadAvatar
                name="avatarUrl"
                maxSize={3145728}
                onDrop={onDropAvatar}
                sx={{ width: 90, height: 90 }}
            />
        </Box>

    );

    const renderList = () => (
        <MenuList
            disablePadding
            sx={[
                (theme) => ({
                    py: 3,
                    px: 2.5,
                    borderTop: `dashed 1px ${theme.vars.palette.divider}`,
                    borderBottom: `dashed 1px ${theme.vars.palette.divider}`,
                    '& li': { p: 0 },
                }),
            ]}
        >
            {data.map((option) => {
                const rootLabel = 'Hem';
                const rootHref = '/';

                return (
                    <MenuItem key={option.label}>
                        <Link
                            component={RouterLink}
                            href={option.label === 'Home' ? rootHref : option.href}
                            color="inherit"
                            underline="none"
                            onClick={onClose}
                            sx={{
                                p: 1,
                                width: 1,
                                display: 'flex',
                                typography: 'body2',
                                alignItems: 'center',
                                color: 'text.secondary',
                                '& svg': { width: 24, height: 24 },
                                '&:hover': { color: 'text.primary' },
                            }}
                        >
                            {option.icon}

                            <Box component="span" sx={{ ml: 2 }}>
                                {option.label === 'Home' ? rootLabel : option.label}
                            </Box>

                            {option.info && (
                                <Label color="error" sx={{ ml: 1 }}>
                                    {option.info}
                                </Label>
                            )}
                        </Link>
                    </MenuItem>
                );
            })}
        </MenuList>
    );

    return (
        <>
            <AccountButton
                onClick={onOpen}
                photoURL={anvandare?.avatarUrl || ''}
                displayName={anvandare?.namn?.förnamn || ''}
                sx={sx}
                {...other}
            />

            <Drawer
                open={open}
                onClose={onClose}
                anchor="right"
                slotProps={{
                    backdrop: { invisible: true },
                    paper: { sx: { width: 320 } },
                }}
            >
                <IconButton
                    onClick={onClose}
                    sx={{
                        top: 12,
                        left: 12,
                        zIndex: 9,
                        position: 'absolute',
                    }}
                >
                    <Iconify icon="mingcute:close-line" />
                </IconButton>

                <Scrollbar>
                    <Form methods={methods}>
                        <Box
                            sx={{
                                pt: 8,
                                display: 'flex',
                                alignItems: 'center',
                                flexDirection: 'column',
                            }}
                        >
                            {renderAvatar()}

                            <Typography variant="subtitle1" noWrap sx={{ mt: 2 }}>
                                {anvandare?.namn?.förnamn} {anvandare?.namn?.efternamn}
                            </Typography>

                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} noWrap>
                                {anvandare?.emailAdress}
                            </Typography>
                        </Box>
                    </Form>

                    <Box
                        sx={{
                            p: 3,
                            gap: 1,
                            flexWrap: 'wrap',
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                    </Box>

                    {renderList()}

                    {/*<Box sx={{ px: 2.5, py: 3 }}>*/}
                    {/*  <UpgradeBlock />*/}
                    {/*</Box>*/}
                </Scrollbar>

                <Box sx={{ p: 2.5 }}>
                    <LoggaUtKnapp onClose={onClose} />
                </Box>
            </Drawer>
        </>
    );
}
